import styled, { css } from "styled-components";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

const SCROLLABLE_CONTENT_MASK_HEIGHT = "48px";

export interface VisibleWindowProps {
  isContentScrollable: boolean;
  showTopFade: boolean;
  showBottomFade: boolean;
}

const buildMaskGradient = (showTopFade: boolean, showBottomFade: boolean) => {
  const topColorStart = showTopFade ? "transparent" : "black";
  const topStop = showTopFade ? SCROLLABLE_CONTENT_MASK_HEIGHT : "0px";
  const bottomStop = showBottomFade ? SCROLLABLE_CONTENT_MASK_HEIGHT : "0px";
  const bottomColorEnd = showBottomFade ? "transparent" : "black";

  return `linear-gradient(
    to bottom,
    ${topColorStart} 0%,
    black ${topStop},
    black calc(100% - ${bottomStop}),
    ${bottomColorEnd} 100%
  )`;
};

export const Root = styled(ScrollAreaPrimitive.Root)`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

export const Viewport = styled(
  ScrollAreaPrimitive.Viewport
)<VisibleWindowProps>`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  /* Force override of Radix's inner wrapper, avoiding horizontal overflow */
  & > div {
    display: block !important;
    min-width: 0 !important;
    width: 100% !important;
  }

  ${({ isContentScrollable, showTopFade, showBottomFade }) => {
    if (!isContentScrollable) return "";
    const gradient = buildMaskGradient(showTopFade, showBottomFade);
    return css`
      -webkit-mask-image: ${gradient};
      mask-image: ${gradient};
      mask-size: 100% 100%;
      mask-repeat: no-repeat;
    `;
  }}
`;

export const Scrollbar = styled(ScrollAreaPrimitive.Scrollbar)`
  display: flex;
  touch-action: none;
  user-select: none;
  transition: background 160ms ease-out;
  background: transparent;

  &[data-orientation="vertical"] {
    width: 8px;
  }

  &[data-orientation="horizontal"] {
    height: 8px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

export const Thumb = styled(ScrollAreaPrimitive.Thumb)`
  flex: 1;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.6);
  }
`;

export const Corner = styled(ScrollAreaPrimitive.Corner)`
  background: transparent;
`;

export const FullContent = styled.div`
  position: relative;
  width: 100%;
`;

export const ScrollSentinel = styled.div`
  position: absolute;
  left: 0;
  width: 100%;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

export const TopScrollSentinel = styled(ScrollSentinel)`
  top: 0;
`;

export const BottomScrollSentinel = styled(ScrollSentinel)`
  bottom: 0;
`;
